import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription, interval } from 'rxjs';
import { ClientSetting, SectionAboutUs } from '../../interfaces/clientSetting.interface';

interface HeroPanel {
  title: string;
  titleSub: string;
  context: string;
  backgroundImage: string;
  context_1: string;
}

export type HeroVariant = 'classic' | 'animated';

/**
 * Hero de la home, con dos plantillas seleccionables por `variant` (hoy vía
 * setting.ts, mañana vía un campo por cliente en section_about_us — todavía no existe
 * ese campo en backend):
 *
 * - 'classic': las 3 cards planas originales (about/tecnology/fairs).
 * - 'animated': hero a pantalla completa de 4 paneles numerados (01-04), con el login
 *   integrado en el panel 1. Referencia: https://hero-block-animate.lovable.app/
 *
 * El panel 4 de 'animated' todavía no tiene su propia clave en `section_about_us`
 * (backend solo maneja about/tecnology/fairs) — hasta que la agreguen, se duplica el
 * contenido de `fairs` como placeholder. Cuando exista la clave nueva, sumarla a
 * `buildPanels()`.
 */
@Component({
  selector: 'app-hero',
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss'],
  animations: [
    // El texto del panel activo (título/subtítulo/contexto) se re-anima cada vez que
    // `activeIndex` cambia de valor — no solo al montar el componente. `'* => *'` matura
    // en cualquier cambio de estado (incluye la primera asignación desde `void`), así que
    // también corre en la carga inicial. Para una transición simple "cambió el valor, quiero
    // repetir la animación de entrada" esto alcanza y sobra: no hace falta la sintaxis
    // `:enter`/`:leave` con `query()`, que sí sería necesaria si el elemento entrara/saliera
    // del DOM (por ejemplo, en una lista con *ngFor).
    trigger('panelText', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('450ms cubic-bezier(0.22, 1, 0.36, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class HeroComponent implements OnChanges, OnInit, OnDestroy {
  @Input() variant: HeroVariant = 'animated';
  @Input() sectionAboutUs: SectionAboutUs | null = null;
  @Input() accentColor: string | null = null;
  @Input() backgroundImageUrl: string = '';
  /** Se lo pasamos a <login>; en 'animated' el form vive dentro del panel 1 del hero. */
  @Input() clientSetting: ClientSetting | null = null;
  /** Solo hacen falta para la plantilla 'classic' (grilla de imágenes de la card "fairs"). */
  @Input() routeUploadFile: string = '';
  @Input() imagesAboutUs: any[] = [];
  @Input() titleContext: any;

  /** 'classic' abre el modal de registro haciendo click en la card del medio; el modal vive en HomeComponent. */
  @Output() openRegisterModal = new EventEmitter<void>();

  public panels: HeroPanel[] = [];
  public activeIndex: number = 0;
  public clientSystemName: string = '';
  public logoPathTwo: string = '';
  public wspNumber: string = '';
  public phoneNumber: string = '';

  /** Posiciones horizontales (%) de las 5 columnas que arman el "barrido" del fondo — cada
   *  una muestra una franja distinta de la misma imagen (ver .hero-bg-col en el scss) y se
   *  revela con un pequeño delay respecto de la anterior, de izquierda a derecha. */
  public readonly bgCols = [0, 25, 50, 75, 100];

  /** z-index por panel (índice = índice de panel). Cada vez que un panel se activa, sube por
   *  encima de todos los demás (ver revealLayer) — así, sin importar en qué orden se navegue
   *  (incluso 04 → 01), el que se activa siempre queda arriba y tapa al anterior mientras barre. */
  public layerZIndex: number[] = [];
  /** Una vez que un panel se revela por primera vez, queda "abierto" (sin volver a animar su
   *  cierre): si se vuelve a mostrar más adelante (autoplay dando otra vuelta), el cambio es
   *  instantáneo en vez de repetir el barrido. Ver nota en resolveBackgroundImage/resetLayers
   *  sobre por qué se eligió esta simplificación. */
  public layerRevealed: boolean[] = [];
  private zCounter = 0;
  private revealTimeoutId?: ReturnType<typeof setTimeout>;

  /** Cada cuánto avanza solo el carrusel de paneles. */
  private readonly autoplayIntervalMs = 6000;
  private autoplaySub?: Subscription;

  ngOnInit(): void {
    this.startAutoplay();
  }

  ngOnDestroy(): void {
    this.autoplaySub?.unsubscribe();
    clearTimeout(this.revealTimeoutId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sectionAboutUs']) {
      this.panels = this.buildPanels();

      const others2 = this.sectionAboutUs?.others_2;

      if(others2) {
        this.panels.push({
        title: others2?.title ?? '',
        titleSub: others2?.title_sub ?? '',
        context: others2?.context ?? '',
        backgroundImage: this.resolveBackgroundImage(others2?.background_image),
        context_1: others2?.context_1 ?? '',
      });
      }
      this.resetLayers();
    }
    if(changes['clientSetting']) {
      this.clientSystemName = this.clientSetting?.name ?? '';
      this.logoPathTwo = `${this.routeUploadFile}${this.clientSetting?.logo_path_two}`;
      const wspNumber = this.clientSetting?.wsp_phone_number ?? '';
      this.wspNumber = this.deleteWhitoutCharacter(wspNumber);
      const phoneNumber = this.clientSetting?.phone_number_one ?? '';
      this.phoneNumber = this.deleteWhitoutCharacter(phoneNumber);
    }
  }

  /** Arranca el autoplay del carrusel; solo tiene sentido en la plantilla 'animated'. */
  private startAutoplay(): void {
    if (this.variant !== 'animated') return;
    this.autoplaySub = interval(this.autoplayIntervalMs).subscribe(() => {
      if (this.panels.length === 0) return;
      this.activeIndex = (this.activeIndex + 1) % this.panels.length;
      this.revealLayer(this.activeIndex);
    });
  }

  /** Se llama cada vez que `panels` se reconstruye (nuevo cliente, o llegó `sectionAboutUs`
   *  después del primer render). Arranca todo cerrado y programa la revelación del panel
   *  activo en el próximo tick — hace falta ese delay para que el navegador pinte el estado
   *  "cerrado" en un frame y recién ahí el cambio a "revelado" dispare la transición CSS; si
   *  se hacen los dos cambios en el mismo ciclo, el navegador los combina y no anima nada. */
  private resetLayers(): void {
    this.layerZIndex = this.panels.map(() => 0);
    this.layerRevealed = this.panels.map(() => false);
    this.zCounter = 0;
    clearTimeout(this.revealTimeoutId);
    this.revealTimeoutId = setTimeout(() => this.revealLayer(this.activeIndex), 60);
  }

  /** Revela el fondo del panel `index` (barrido de columnas) y lo trae al frente. Una vez
   *  revelado queda así — no vuelve a cerrarse — así que reactivarlo más adelante (por
   *  ejemplo, el autoplay dando otra vuelta) solo lo trae al frente al instante, sin repetir
   *  el barrido. Es la simplificación consciente: repetir el barrido en cada revisita pide
   *  reciclar dos capas fijas (una "actual" congelada + una "entrante" que se resetea sola
   *  recién cuando queda 100% tapada) para evitar que el cierre de la capa vieja y la
   *  apertura de la nueva, animando a la vez, dejen un hueco visible a mitad de camino. */
  private revealLayer(index: number): void {
    if (!this.panels[index]) return;
    this.zCounter += 1;
    this.layerZIndex[index] = this.zCounter;
    this.layerRevealed[index] = true;
  }

  /** Reinicia el conteo del autoplay — se llama cuando el usuario cambia de panel a mano,
   *  para que no "compita" con el próximo tick automático inmediatamente después del click. */
  private restartAutoplay(): void {
    this.autoplaySub?.unsubscribe();
    this.startAutoplay();
  }

  deleteWhitoutCharacter(value: string): string {

    if(!value) {
      return '';
    }
    return value.replace(/\+/g, '').replace(/\s/g, '');
  }

  private buildPanels(): HeroPanel[] {
    const about = this.sectionAboutUs?.about;
    const tecnology = this.sectionAboutUs?.tecnology;
    const fairs = this.sectionAboutUs?.fairs;
    const others = this.sectionAboutUs?.others;

    return [
      {
        title: about?.title ?? '',
        titleSub: about?.title_sub ?? '',
        context: about?.context ?? '',
        backgroundImage: this.resolveBackgroundImage(about?.background_image),
        context_1: about?.context_1 ?? '',
      },
      {
        title: tecnology?.title ?? '',
        titleSub: tecnology?.title_sub ?? '',
        context: tecnology?.context ?? '',
        backgroundImage: this.resolveBackgroundImage(tecnology?.background_image),
        context_1: tecnology?.context_1 ?? '',
      },
      {
        title: fairs?.title ?? '',
        titleSub: fairs?.title_sub ?? '',
        context: fairs?.context ?? '',
        backgroundImage: this.resolveBackgroundImage(fairs?.background_image),
        context_1: fairs?.context_1 ?? '',
      },
      // TODO: placeholder — duplica "fairs" hasta que backend sume una 4ta clave a section_about_us.
      {
        title: others?.title ?? '',
        titleSub: others?.title_sub ?? '',
        context: others?.context ?? '',
        backgroundImage: this.resolveBackgroundImage(others?.background_image),
        context_1: others?.context_1 ?? '',
      }
    ];

  }

  /** `about`/`tecnology` traen un único path; `fairs.background_image` puede venir como
   *  array (grilla de imágenes de la card vieja) — en ese caso se usa la primera como fondo
   *  del panel. Si el panel no trae imagen propia, cae a `backgroundImageUrl` (la imagen del
   *  header que ya se usaba antes de tener un fondo por panel). */
  private resolveBackgroundImage(image: string | readonly string[] | undefined): string {
    const path = Array.isArray(image) ? image[0] : image;
    return path ? `url("${this.routeUploadFile}${path}")` : this.backgroundImageUrl;
  }

  setActive(index: number): void {
    this.activeIndex = index;
    this.revealLayer(index);
    this.restartAutoplay();
  }

  pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  getWidthHeroTitleAnimated(index: number): string {

    let width: string = '0';

    switch(index) {
      case 0: width = '72%'; break ;
      case 1: width = '72%'; break ;
      case 2: width = '73%'; break ;
      case 3: width = '80%'; break ;
      case 4: width = '75%'; break ;
    }

    return width;
  }
}
